import React, { useState } from "react";
import { Globe, Mail, MapPin, Building2, Loader2, Send, CheckCircle2 } from "lucide-react";
import LegalLayout from "@/pages/legal/LegalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const rows = [
  { icon: Globe, label: "Website", value: "www.blueshuckconsulting.com", href: "https://www.blueshuckconsulting.com" },
  { icon: Mail, label: "Email", value: "support@blueshuckconsulting.com", href: "mailto:support@blueshuckconsulting.com" },
  { icon: MapPin, label: "Address", value: "Blue Shuck Consulting Ltd, Collingwood Buildings, 38 Collingwood Street, Newcastle, NE1 1JF" },
  { icon: Building2, label: "Companies House Number", value: "17308746" },
];

const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      setSent(true);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send. Please email us instead."); }
    finally { setSending(false); }
  };

  if (sent)
    return (
      <div className="mt-8 rounded-xl border border-green-200 bg-green-50 p-6 flex items-center gap-3" data-testid="lead-form-success">
        <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
        <p className="text-slate-700 text-sm m-0">Thanks for reaching out. We've got your message and will be in touch shortly.</p>
      </div>
    );

  return (
    <form onSubmit={submit} className="mt-8 rounded-xl border border-slate-200 p-6 space-y-4 bg-white" data-testid="lead-form">
      <h3 className="font-display text-lg font-bold text-slate-900 m-0">Send us a message</h3>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><Label htmlFor="lead-name">Name</Label><Input id="lead-name" required value={form.name} onChange={set("name")} data-testid="lead-name" className="mt-1" /></div>
        <div><Label htmlFor="lead-email">Email</Label><Input id="lead-email" type="email" required value={form.email} onChange={set("email")} data-testid="lead-email" className="mt-1" /></div>
      </div>
      <div><Label htmlFor="lead-company">Company (optional)</Label><Input id="lead-company" value={form.company} onChange={set("company")} data-testid="lead-company" className="mt-1" /></div>
      <div><Label htmlFor="lead-message">How can we help?</Label><Textarea id="lead-message" rows={4} value={form.message} onChange={set("message")} data-testid="lead-message" className="mt-1" /></div>
      <Button type="submit" disabled={sending} data-testid="lead-submit" className="bg-blue-600 hover:bg-blue-700">
        {sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />} Send message
      </Button>
    </form>
  );
};

const Contact = () => (
  <LegalLayout title="Contact Us" subtitle="We'd love to hear from you.">
    <p>Have a question about TradeOff, need a hand getting set up, or want to talk about your projects? Get in touch using the details below and we'll come back to you as soon as we can.</p>
    <div className="mt-6 rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden" data-testid="contact-details">
      {rows.map((r) => (
        <div key={r.label} className="flex items-start gap-4 p-4">
          <span className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><r.icon className="h-5 w-5 text-blue-600" /></span>
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{r.label}</p>
            {r.href ? <a href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-slate-800 font-medium">{r.value}</a>
              : <p className="text-slate-800 font-medium">{r.value}</p>}
          </div>
        </div>
      ))}
    </div>
    <LeadForm />
    <p className="mt-6 text-sm text-slate-500">Blue Shuck Consulting Ltd is a company registered in England and Wales.</p>
  </LegalLayout>
);

export default Contact;
